<template>
  <g v-if="isVisible" :class="{ 'remove-cursor': !isDefaultCursor }">
    <line
      v-if="localPoints.length > 0"
      :x1="localPoints[0][0]"
      :y1="localPoints[0][1]"
      :x2="localPoints[1][0]"
      :y2="localPoints[1][1]"
      stroke="transparent"
      stroke-width="8"
      class="horizontal"
      @mousedown="startDrag($event, 0)"
      @click="setArrowSelected"
    />
    <line
      v-if="localPoints.length > 0"
      :x1="localPoints[1][0]"
      :y1="localPoints[1][1]"
      :x2="localPoints[2][0]"
      :y2="localPoints[2][1]"
      stroke="transparent"
      stroke-width="8"
      class="vertical"
      @mousedown="startDrag($event, 1)"
      @click="setArrowSelected"
    />
    <line
      v-if="localPoints.length > 0"
      :x1="localPoints[2][0]"
      :y1="localPoints[2][1]"
      :x2="localPoints[3][0]"
      :y2="localPoints[3][1]"
      stroke="transparent"
      stroke-width="8"
      class="horizontal"
      @mousedown="startDrag($event, 2)"
      @click="setArrowSelected"
    />
  </g>
</template>

<script>
import { CustomCursorTypes } from "../../../../../../utils/constants";
import DesignerMixin from "../../../../DesignerMixin";
import { RADIUS, SPACE_BETWEEN_VLINES } from "../ElementUtils";

export default {
  mixins: [DesignerMixin],
  props: ["id", "attributes", "startShapeId", "endShapeId"],
  data() {
    return {
      dragging: false,
      localPoints: [], // all connection points in array of points format Example: [[x,y],[x,y]]
      line: {}, // line overlays on top of two vertical points. necessary for having different cursor types and movement check
      lineNumber: 0,
      startIsAtCenter: null,
      endIsAtCenter: null,
      pflowLeft: null,
      pflowTop: null,
    };
  },
  computed: {
    isVisible() {
      return (
        this.getRowVisibility(this.startShapeId.replace("activity_", "")) &&
        this.getRowVisibility(this.endShapeId.replace("activity_", ""))
      );
    },
    startShape() {
      return this.$store.state.customFlow.shapeMap[this.startShapeId];
    },
    endShape() {
      return this.$store.state.customFlow.shapeMap[this.endShapeId];
    },
    shapes() {
      return this.$store.state.customFlow.shapes;
    },
    isDefaultCursor() {
      return (
        this.$store.state.cells.arrow.activeCursor === CustomCursorTypes.Default &&
        this.$store.state.customFlow.newArrow.activeCursor === CustomCursorTypes.Default
      );
    },
  },
  watch: {
    startShape: {
      handler: function (val, oldVal) {
        this.copyAttributes();
      },
      deep: true,
    },
    endShape: {
      handler: function (val, oldVal) {
        this.copyAttributes();
      },
      deep: true,
    },
  },
  mounted() {
    this.copyAttributes(this.attributes);
  },
  beforeUnmount() {
    document.removeEventListener("mousemove", this.drag);
    document.removeEventListener("mouseup", this.endDrag);
  },
  methods: {
    getPointStr(points) {
      return points.reduce((prev, current) => prev + current[0] + " " + current[1] + " ", "");
    },
    getLine(points) {
      return {
        points: this.attributes[1],
      };
    },
    calculateEndPoint() {
      if (this.lineNumber === 0) this.setStartPointVertically();
      if (this.lineNumber === 1) {
        this.setStartPointPosition();
        this.setEndPointPosition();
      }
      if (this.lineNumber === 2) this.setEndPointVertically();
    },
    copyAttributes() {
      this.localPoints = [...this.attributes];
    },
    startDrag(e, lineNumber) {
      this.lineNumber = lineNumber;
      this.dragging = true;

      const selectedShapeId = lineNumber === 0 ? this.startShapeId : lineNumber === 2 ? this.endShapeId : null;

      const pflow = document.querySelector(".flow").getBoundingClientRect();
      this.pflowLeft = pflow.left;
      this.pflowTop = pflow.top;

      this.$store.dispatch("customFlow/resetSelection");
      this.$store.dispatch("customFlow/setCluePointsId", selectedShapeId);
      this.$store.dispatch("customFlow/setArrowDraggingId", this.id);
      document.addEventListener("mousemove", this.drag);
      document.addEventListener("mouseup", this.endDrag);
    },
    endDrag(e) {
      this.dragging = false;
      document.removeEventListener("mousemove", this.drag);
      document.removeEventListener("mouseup", this.endDrag);

      let lineNo = Math.floor(this.localPoints[1][0] / (SPACE_BETWEEN_VLINES / 2));
      let reminder = this.localPoints[1][0] % (SPACE_BETWEEN_VLINES / 2);
      let position =
        reminder > SPACE_BETWEEN_VLINES / 4
          ? (lineNo + 1) * (SPACE_BETWEEN_VLINES / 2)
          : lineNo * (SPACE_BETWEEN_VLINES / 2);

      this.updateLocalPointsX(position);
      this.$store.dispatch("customFlow/updateConnection", {
        id: this.id,
        points: this.localPoints,
      });

      this.copyAttributes();
      this.$store.dispatch("customFlow/setArrowDraggingId", null);
      this.$store.dispatch("customFlow/persist");
      this.$store.dispatch("customFlow/resetCluePointsId");
    },
    drag(e) {
      if (this.lineNumber === 1) {
        this.updateLocalPointsX(e.clientX - this.pflowLeft);
      } else {
        if (this.lineNumber === 0) this.startIsAtCenter = false;
        if (this.lineNumber === 2) this.endIsAtCenter = false;
        this.updateLocalPointsY(e.clientY - this.pflowTop);
      }

      this.calculateEndPoint();
      this.$store.dispatch("customFlow/updateConnection", {
        id: this.id,
        points: this.localPoints,
      });
    },

    updateLocalPointsX(cx) {
      if (cx < SPACE_BETWEEN_VLINES / 4 || cx >= this.processFlowWidth - SPACE_BETWEEN_VLINES / 4) return;
      this.localPoints[1][0] = cx;
      this.localPoints[2][0] = cx;
    },

    updateLocalPointsY(cy) {
      if (this.lineNumber === 0) {
        this.localPoints[0][1] = cy;
        this.localPoints[1][1] = cy;
      } else if (this.lineNumber === 2) {
        this.localPoints[2][1] = cy;
        this.localPoints[3][1] = cy;
      }
    },

    setStartPointPosition() {
      const startShapeX = this.startShape.attributes.cx;
      const startShapeY = this.startShape.attributes.cy;
      const endShapeX = this.endShape.attributes.cx;
      const endShapeY = this.endShape.attributes.cy;

      const changingPointStart = this.localPoints[1];
      const changingPointEnd = this.localPoints[2];

      const firstPoint = this.localPoints[0];
      // if center
      if (Math.abs(this.localPoints[1][0] - startShapeX) < RADIUS + 4) {
        this.startIsAtCenter = true;
        if (startShapeY < endShapeY) {
          changingPointStart[0] = startShapeX;
          changingPointStart[1] = startShapeY + RADIUS;
          firstPoint[0] = startShapeX;
          firstPoint[1] = startShapeY + RADIUS;
          changingPointEnd[0] = startShapeX;
        } else {
          changingPointStart[0] = startShapeX;
          changingPointStart[1] = startShapeY - RADIUS;
          firstPoint[0] = startShapeX;
          firstPoint[1] = startShapeY - RADIUS;
          changingPointEnd[0] = startShapeX;
        }
      }
      // if to right
      else if (changingPointStart[0] > startShapeX) {
        if (this.startIsAtCenter) {
          // do not change if it is at 1:30, 4:30, 7:30, or 10:30. Only change if it is at center.
          changingPointStart[1] = startShapeY;
          firstPoint[0] = startShapeX + RADIUS;
          firstPoint[1] = startShapeY;
        }
      } // if to left
      else {
        if (this.startIsAtCenter) {
          changingPointStart[1] = startShapeY;
          firstPoint[0] = startShapeX - RADIUS;
          firstPoint[1] = startShapeY;
        }
      }
    },
    setEndPointPosition() {
      const startShapeX = this.startShape.attributes.cx;
      const startShapeY = this.startShape.attributes.cy;
      const endShapeX = this.endShape.attributes.cx;
      const endShapeY = this.endShape.attributes.cy;
      const changingPointEnd = this.localPoints[2];
      const lastPoint = this.localPoints[this.localPoints.length - 1];
      const changingPointStart = this.localPoints[1];
      // if center
      if (Math.abs(changingPointEnd[0] - endShapeX) < RADIUS + 4) {
        this.endIsAtCenter = true;
        if (startShapeY < endShapeY) {
          changingPointEnd[0] = endShapeX;
          changingPointEnd[1] = endShapeY - RADIUS - 1;
          lastPoint[0] = endShapeX;
          lastPoint[1] = endShapeY - RADIUS;

          changingPointStart[0] = endShapeX;
        } else {
          changingPointEnd[0] = endShapeX;
          changingPointEnd[1] = endShapeY + RADIUS + 1;
          lastPoint[0] = endShapeX;
          lastPoint[1] = endShapeY + RADIUS;

          changingPointStart[0] = endShapeX;
        }
      }
      // if to right
      else if (this.localPoints[2][0] > endShapeX) {
        if (this.endIsAtCenter) {
          changingPointEnd[1] = endShapeY;
          lastPoint[0] = endShapeX + RADIUS;
          lastPoint[1] = endShapeY;
        }
      } // if to left
      else {
        if (this.endIsAtCenter) {
          changingPointEnd[1] = endShapeY;
          lastPoint[0] = endShapeX - RADIUS;
          lastPoint[1] = endShapeY;
        }
      }
    },

    setPointVertically(shapeX, shapeY, changingPointStart, changingPointEnd) {
      const calculateCoordinates = (offsetX, offsetY) => {
        changingPointStart[1] = shapeY + offsetY;
        changingPointEnd[1] = shapeY + offsetY;
        changingPointEnd[0] = shapeX + offsetX;
      };

      if (changingPointEnd[1] < shapeY) {
        if (changingPointEnd[0] >= shapeX) {
          calculateCoordinates(RADIUS - 2, -RADIUS + 2); // 1:30
        } else {
          calculateCoordinates(-RADIUS + 2, -RADIUS + 2); // 10:30
        }
      }

      if (changingPointEnd[1] < shapeY + 4 && changingPointEnd[1] > shapeY - 4) {
        // 4 is like padding to select easily 3:00 and 9:00
        if (changingPointEnd[0] >= shapeX) {
          calculateCoordinates(RADIUS, 0); // 3:00
        } else {
          calculateCoordinates(-RADIUS, 0); // 9:00
        }
      }

      if (changingPointEnd[1] > shapeY) {
        if (changingPointEnd[0] >= shapeX) {
          calculateCoordinates(RADIUS - 2, RADIUS - 2); // 4:30
        } else {
          calculateCoordinates(-RADIUS + 2, RADIUS - 2); // 7:30
        }
      }
    },

    setEndPointVertically() {
      const endShapeX = this.endShape.attributes.cx;
      const endShapeY = this.endShape.attributes.cy;
      const changingPointStart = this.localPoints[2];
      const changingPointEnd = this.localPoints[3];

      this.setPointVertically(endShapeX, endShapeY, changingPointStart, changingPointEnd);
    },

    setStartPointVertically() {
      const startShapeX = this.startShape.attributes.cx;
      const startShapeY = this.startShape.attributes.cy;
      const changingPointStart = this.localPoints[1];
      const changingPointEnd = this.localPoints[0];

      this.setPointVertically(startShapeX, startShapeY, changingPointStart, changingPointEnd);
    },
    setArrowSelected() {
      this.$store.dispatch("customFlow/setArrowSelection", {
        id: this.id,
        attributes: this.localPoints,
      });
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../../../../assets/styles/variables";

.vertical {
  cursor: e-resize;
}
.horizontal {
  cursor: n-resize;
}
.remove-cursor {
  pointer-events: none;
}
</style>
